import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IteamState } from "./care-team.types";
import axiosInstance from "config/axios";

const initialState: IteamState = {
  team: [],
  singleTeam: {
    _id: "",
    creatorId: "",
    hospitalName: "",
    email: "",
    veterinarianName: "",
    phone: "",
    zipCode: "",
    apartment: "",
    state: "",
    city: "",
    address: "",
    address2: "",
  },
  teamError: {},
  isTeamActivityInProcess: false,
  isTeamDeleteInProcess: false,
  teamSuccessMesssage: "",
};

export const getUserteams = createAsyncThunk("getUserteams/team", async (_, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance().get("/careteam/get-all-careteam");

    return data;
  } catch (error: any) {
    return rejectWithValue(error.response);
  }
});

// this is for admin without sending user auth token.
export const getCareTeamWithUserId = createAsyncThunk(
  "getCareTeamWithUserId/team",
  async (userId: any, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance().get(`/careteam/get-user-careteam/${userId}`);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response);
    }
  }
);

// this is for users with user auth token.
export const getSingleteam = createAsyncThunk("getSingleteam/team", async (teamID: any, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance().get(`/careteam/get-careteam/${teamID}`);

    return data;
  } catch (error: any) {
    return rejectWithValue(error.response);
  }
});

export const updateSingleteam = createAsyncThunk("updateSingleteam/team", async (team: any, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance().put(`/careteam/update-careteam/${team._id}`, team);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response);
  }
});

export const deleteSingleteam = createAsyncThunk("deleteSingleteam/team", async (teamID: any, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance().delete(`/careteam/delete-careteam/${teamID}`);

    return data;
  } catch (error: any) {
    return rejectWithValue(error.response);
  }
});

export const addNewteam = createAsyncThunk("addNewteam/team", async (team: any, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance().post(`/careteam/add-careteam`, team);

    return data;
  } catch (error: any) {
    return rejectWithValue(error.response);
  }
});

export const addNewteamByAdmin = createAsyncThunk("addNewteamByAdmin/team", async (team: any, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance().post(`/careteam/add-careteam-by-admin`, team);

    return data;
  } catch (error: any) {
    return rejectWithValue(error.response);
  }
});

const teamSlice = createSlice({
  name: "teamSlice",
  initialState: { ...initialState, name: "teamInitialState" },
  reducers: {
    errorClean: (state) => {
      state.teamError = {
        code: null,
        message: "",
        error: "",
      };
    },
    messageClean: (state) => {
      state.teamSuccessMesssage = "";
    },
  },
  extraReducers: (builder) => {
    // get all teams cases
    builder.addCase(getUserteams.pending, (state, action: any) => {
      state.isTeamActivityInProcess = true;
    });

    builder.addCase(getUserteams.rejected, (state, action: any) => {
      state.isTeamActivityInProcess = false;
      state.teamError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(getUserteams.fulfilled, (state, action: any) => {
      state.isTeamActivityInProcess = false;

      state.team = action.payload.careTeams;
    });

    // get single team by teamID cases
    builder.addCase(getSingleteam.pending, (state, action: any) => {
      state.isTeamActivityInProcess = true;
    });

    builder.addCase(getSingleteam.rejected, (state, action: any) => {
      state.isTeamActivityInProcess = false;
      state.teamError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(getSingleteam.fulfilled, (state, action: any) => {
      state.isTeamActivityInProcess = false;
      state.singleTeam = action.payload.careTeam;
    });

    // get single team by userId without token
    builder.addCase(getCareTeamWithUserId.pending, (state, action: any) => {
      state.isTeamActivityInProcess = true;
    });

    builder.addCase(getCareTeamWithUserId.rejected, (state, action: any) => {
      state.isTeamActivityInProcess = false;
      state.teamError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(getCareTeamWithUserId.fulfilled, (state, action: any) => {
      state.isTeamActivityInProcess = false;
      state.careTeams = action.payload.careTeams;
    });

    // update single team by teamID cases
    builder.addCase(updateSingleteam.pending, (state, action: any) => {
      state.isTeamActivityInProcess = true;
    });

    builder.addCase(updateSingleteam.rejected, (state, action: any) => {
      state.isTeamActivityInProcess = false;
      state.teamError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(updateSingleteam.fulfilled, (state, action: any) => {
      state.isTeamActivityInProcess = false;
      state.teamSuccessMesssage = action.payload.message;

      state.singleTeam = action.payload.team;
    });

    // delete single team by teamID cases
    builder.addCase(deleteSingleteam.pending, (state, action: any) => {
      state.isTeamDeleteInProcess = true;
    });

    builder.addCase(deleteSingleteam.rejected, (state, action: any) => {
      state.isTeamDeleteInProcess = false;
      state.teamError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(deleteSingleteam.fulfilled, (state, action: any) => {
      state.isTeamDeleteInProcess = false;
      state.teamSuccessMesssage = action.payload.message;

      state.singleTeam = action.payload.team;
    });

    // Add New team cases
    builder.addCase(addNewteam.pending, (state, action: any) => {
      state.isTeamActivityInProcess = true;
    });

    builder.addCase(addNewteam.rejected, (state, action: any) => {
      state.isTeamActivityInProcess = false;
      state.teamError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(addNewteam.fulfilled, (state, action: any) => {
      state.isTeamActivityInProcess = false;
      state.teamSuccessMesssage = action.payload.message;

      state.singleTeam = action.payload.team;
    });

    // add careTeam by admin

    builder.addCase(addNewteamByAdmin.pending, (state, action: any) => {
      state.isTeamActivityInProcess = true;
    });

    builder.addCase(addNewteamByAdmin.rejected, (state, action: any) => {
      state.isTeamActivityInProcess = false;
      state.teamError = {
        code: action.payload.status,
        message: action.payload.data.message,
      };
    });

    builder.addCase(addNewteamByAdmin.fulfilled, (state, action: any) => {
      state.isTeamActivityInProcess = false;
      state.teamSuccessMesssage = action.payload.message;
      state.singleTeam = action.payload.team;
    });
  },
});
export const { errorClean, messageClean } = teamSlice.actions;

export default teamSlice.reducer;
